<template>
    <form method="POST" accept-charset="UTF-8" novalidate :data-vv-scope="uid" @submit.prevent="validateBeforeSubmit">
        <slot v-if="showMessages && messagePosition === 'above-content'" name="messages" :success="success" :success-message="successMessage" :error-message="errorMessage">
            <div v-if="(success && !redirect)" class="valid-feedback">{{ successMessage }}</div>
            <div v-if="errorMessage" class="invalid-feedback">{{ errorMessage }}</div>
        </slot>

        <slot :uid="uid"></slot>

        <slot v-if="showMessages && messagePosition === 'below-content'" name="messages" :success="success" :success-message="successMessage" :error-message="errorMessage">
            <div v-if="(success && !redirect)" class="valid-feedback">{{ successMessage }}</div>
            <div v-if="errorMessage" class="invalid-feedback">{{ errorMessage }}</div>
        </slot>

        <slot name="submit" :loading="loading" :success="success" :error="error">
            <button type="submit" :disabled="!!loading" :class="['btn btn-primary', { 'loading': loading }]">
                {{ buttonText }}
            </button>
        </slot>

        <slot v-if="showMessages && messagePosition === 'below-submit'" name="messages" :success="success" :success-message="successMessage" :error-message="errorMessage">
            <div v-if="(success && !redirect)" class="valid-feedback">{{ successMessage }}</div>
            <div v-if="errorMessage" class="invalid-feedback">{{ errorMessage }}</div>
        </slot>
    </form>
</template>

<script>
import { takeAtLeast } from '../utils/promise';

export default {
    inject: {
        $validator: '$validator',
    },

    props: {
        name: {
            type: String,
            required: true,
        },

        action: {
            type: String,
            default: '/',
        },

        buttonText: {
            type: String,
            default: 'Submit',
        },

        successMessage: {
            type: String,
            default: 'Updated',
        },

        successButtonText: {
            type: String,
            default: '',
        },

        errorButtonText: {
            type: String,
            default: '',
        },

        showMessages: {
            type: Boolean,
            default: true,
        },

        messagePosition: {
            validator(value) {
                return ['above-content', 'below-content', 'below-submit'].indexOf(value) !== -1;
            },
            default: 'above-content',
        },

        buttonTimeout: {
            type: Number,
            default: 0,
        },

        redirect: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            success: false,
            loading: false,
            error: false,
            errorMessage: '',
        };
    },

    methods: {
        validateBeforeSubmit() {
            this.loading = true;

            this.$validator.validateAll(this.uid).then((isValid) => {
                if (!isValid) {
                    this.loading = false;

                    return;
                }

                this.submit();
            });
        },

        submit() {
            const formData = new FormData(this.$el);

            // Reset state
            this.loading = true;
            this.error = false;
            this.success = false;

            this.$axios.post(this.action, formData).then(takeAtLeast(300)).then((response) => {
                const { data } = response;

                // Just double-check its all good
                if (data.error) {
                    throw new Error(data.error);
                }

                // Emit a global event
                this.$events.$emit(`form:${this.$el.name}-success`, { response, formData });

                // Emit a local event
                this.$emit('form:success', { response, formData });

                // We might want to verify the response data a little...
                if (this.redirect) {
                    return window.location.replace(this.redirect);
                }

                // Continue to show loading if redirecting - looks nicer
                this.loading = false;
                this.success = true;

                // Fade out the button, if set
                if (this.buttonTimeout) {
                    setTimeout(() => {
                        this.success = false;
                    }, this.buttonTimeout);
                }

                console.log(data);
            })
                .catch((error) => {
                    this.loading = false;
                    this.error = true;

                    let message = 'An error occured';

                    if (error.response) {
                        // Received a non-2xx response from the server
                        console.log(error.response.data);

                        // TODO: might need to be updated to cater for all scenarios
                        message = error.response.data.error;
                    } else if (error.request) {
                        // The request was made but no response was received
                        console.log(error.request);

                        message = error.request;
                    } else {
                        // Something happened in setting up the request that triggered an Error
                        console.log('Error', error.message);

                        // Compiler complains about destructuring?
                        const { message } = error.message;
                    }

                    this.errorMessage = message;

                    // Emit a global event
                    this.$events.$emit(`form:${this.$el.name}-error`, { response: error.response, formData });

                    // Emit a local event
                    this.$emit('form:error', { response: error.response, formData });

                    // Fade out the button, if set
                    if (this.buttonTimeout) {
                        setTimeout(() => {
                            this.error = false;
                        }, this.buttonTimeout);
                    }

                    console.log(error.config);
                });
        },
    },
};

</script>
